import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useBackHomePath } from "@/lib/useBackHomePath";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Lightbulb, Filter, Trash2, Paperclip, Link as LinkIcon, Upload, FileText, Plus, ExternalLink, Send } from "lucide-react";
import { toast } from "sonner";

const PLANS = [
    { value: "A", label: "Plan A", desc: "Mbështetje e lehtë", chip: "bg-sage/15 text-sage border-sage/30" },
    { value: "B", label: "Plan B", desc: "Mbështetje moderate", chip: "bg-amber-50 text-amber-700 border-amber-200" },
    { value: "C", label: "Plan C", desc: "Ndërhyrje intensive", chip: "bg-terracotta/10 text-terracotta border-terracotta/30" },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const formatSize = (b) => {
    if (!b) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const emptyForm = () => ({ student_id: "", subject_id: "", plan_level: "A", note: "", resources: [] });

function ResourceList({ resources, onRemove }) {
    if (!resources.length) return null;
    return (
        <div className="space-y-2 mt-2">
            {resources.map((r, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 bg-bone/40 rounded-md border border-slate-200" data-testid={`resource-${i}`}>
                    {r.type === "link" ? <LinkIcon className="w-4 h-4 text-oxford shrink-0" /> : <FileText className="w-4 h-4 text-sage shrink-0" />}
                    <div className="flex-1 min-w-0 text-sm">
                        <div className="font-semibold text-slate-800 truncate">{r.label || r.filename || r.url || "Resurs"}</div>
                        <div className="text-[11px] text-slate-500 truncate">
                            {r.type === "link" ? r.url : `${r.filename || "File"}${r.size ? ` • ${formatSize(r.size)}` : ""}`}
                        </div>
                    </div>
                    {onRemove && (
                        <button type="button" onClick={() => onRemove(i)} className="p-1.5 rounded-md text-red-500 hover:bg-red-50" data-testid={`resource-remove-${i}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}

export default function TeacherStudentPlans() {
    const [plans, setPlans] = useState([]);
    const [students, setStudents] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [classes, setClasses] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [filterClass, setFilterClass] = useState("");
    const [filterSubject, setFilterSubject] = useState("");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [linkInput, setLinkInput] = useState({ url: "", label: "" });
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const backPath = useBackHomePath();

    const refresh = async () => {
        const [p, s, subs, c, t] = await Promise.all([
            api.get("/teacher/student-plans"),
            api.get("/teacher/students"),
            api.get("/teacher/my-subjects"),
            api.get("/classes"),
            api.get("/teacher/plan-templates"),
        ]);
        setPlans(p.data); setStudents(s.data); setSubjects(subs.data); setClasses(c.data); setTemplates(t.data);
    };
    useEffect(() => { refresh(); }, []);

    // Templates filtered by selected plan_level
    const templatesForLevel = useMemo(
        () => templates.filter((t) => t.plan_level === form.plan_level),
        [templates, form.plan_level]
    );

    const applyTemplate = (tplId) => {
        if (!tplId) return;
        const tpl = templates.find((t) => t.id === tplId);
        if (!tpl) return;
        setForm((f) => ({
            ...f,
            note: tpl.note || f.note,
            // Append template resources (avoid duplicates by URL)
            resources: [
                ...f.resources,
                ...(tpl.resources || []).filter((r) => !f.resources.some((existing) => existing.url === r.url && existing.type === r.type)),
            ],
        }));
        toast.success(`Shablloni '${tpl.title}' u aplikua`);
    };

    const filtered = useMemo(() => plans.filter((p) => (!filterClass || p.class_id === filterClass) && (!filterSubject || p.subject_id === filterSubject)), [plans, filterClass, filterSubject]);
    const stats = useMemo(() => {
        const m = { A: 0, B: 0, C: 0 };
        for (const p of filtered) m[p.plan_level] += 1;
        return m;
    }, [filtered]);

    const addLink = () => {
        if (!linkInput.url.trim()) return toast.error("URL e detyrueshme");
        const url = linkInput.url.trim();
        if (!/^https?:\/\//i.test(url)) return toast.error("URL duhet të fillojë me http(s)://");
        setForm((f) => ({ ...f, resources: [...f.resources, { type: "link", url, label: linkInput.label.trim() || url }] }));
        setLinkInput({ url: "", label: "" });
    };

    const uploadFile = async (file) => {
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/lms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((f) => ({
                ...f,
                resources: [...f.resources, {
                    type: "file",
                    file_id: r.data.file_id,
                    filename: r.data.filename,
                    size: r.data.size,
                    label: r.data.filename,
                    content_type: r.data.content_type,
                }],
            }));
            toast.success("Skedari u ngarkua");
        } catch (e) {
            toast.error("Ngarkimi dështoi: " + (e.response?.data?.detail || e.message));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const removeResource = (idx) => setForm((f) => ({ ...f, resources: f.resources.filter((_, i) => i !== idx) }));

    const submit = async (e) => {
        e.preventDefault(); setBusy(true);
        try {
            // strip client-only fields before sending (the server re-enriches on read)
            const payload = {
                ...form,
                resources: form.resources.map((r) => ({
                    type: r.type,
                    url: r.url || null,
                    file_id: r.file_id || null,
                    label: r.label || "",
                })),
            };
            await api.post("/teacher/student-plan", payload);
            setOpen(false);
            setForm(emptyForm());
            refresh();
            toast.success("Plani u ruajt");
        } catch (e2) {
            toast.error("Gabim: " + (e2.response?.data?.detail || e2.message));
        } finally { setBusy(false); }
    };

    const del = async (pid) => {
        if (!window.confirm("Hiq këtë plan?")) return;
        await api.delete(`/teacher/student-plan/${pid}`);
        refresh();
    };

    const fileHref = (file_id) => {
        const token = localStorage.getItem("token");
        return `${BACKEND}/api/lms/file/${file_id}?auth=${encodeURIComponent(token || "")}`;
    };

    return (
        <Layout>
            <Link to={backPath} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Plane për nxënësit</div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Planet A / B / C</h1>
                    <p className="text-slate-600 mt-2 text-sm">Cakto nivelin e mbështetjes për çdo nxënës dhe bashkangjit materiale (link ose PDF/PPT).</p>
                </div>
                <Button onClick={() => { setForm(emptyForm()); setOpen(true); }} className="bg-oxford hover:bg-oxfordhover text-white gap-2" data-testid="sp-new-btn">
                    <Lightbulb className="w-4 h-4" /> Cakto Plan
                </Button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-5">
                {PLANS.map((pl) => (
                    <div key={pl.value} className={`bg-white border-2 rounded-xl p-4 ${pl.value === "A" ? "border-sage/40" : pl.value === "B" ? "border-amber-200" : "border-terracotta/40"}`}>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{pl.label}</div>
                        <div className="font-heading text-3xl font-black mt-1" data-testid={`sp-count-${pl.value}`}>{stats[pl.value]}</div>
                        <div className="text-[11px] text-slate-500">{pl.desc}</div>
                    </div>
                ))}
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-3 mb-5 flex flex-wrap items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white text-sm px-2" data-testid="sp-filter-class">
                    <option value="">Të gjitha klasat</option>
                    {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select value={filterSubject} onChange={(e) => setFilterSubject(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white text-sm px-2" data-testid="sp-filter-subject">
                    <option value="">Të gjitha lëndët</option>
                    {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {(filterClass || filterSubject) && <button onClick={() => { setFilterClass(""); setFilterSubject(""); }} className="text-xs text-terracotta hover:underline ml-auto">Pastro</button>}
            </div>

            {filtered.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
                    <Lightbulb className="w-10 h-10 text-slate-400 mx-auto mb-4" />
                    <div className="font-heading text-xl font-bold text-slate-800">
                        {plans.length === 0 ? "Ti nuk ke rekomanduar asnjë plan ende" : "Pa plane në këtë filtër"}
                    </div>
                    {plans.length === 0 && <p className="text-sm text-slate-600 mt-1">Klik "Cakto Plan" për të rekomanduar Plan A/B/C për nxënësit e tu.</p>}
                </div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="divide-y divide-slate-200">
                        {filtered.map((p) => {
                            const meta = PLANS.find((pp) => pp.value === p.plan_level) || PLANS[0];
                            return (
                                <div key={p.id} className="px-4 sm:px-5 py-3" data-testid={`sp-row-${p.id}`}>
                                    <div className="flex items-center gap-3">
                                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full border font-heading font-black ${meta.chip}`}>{meta.label}</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-slate-900 truncate">{p.student_name}</div>
                                            <div className="text-[11px] text-slate-500 truncate">
                                                {p.class_name} • {p.subject_name}
                                                {p.note && <span> — {p.note}</span>}
                                            </div>
                                        </div>
                                        <button onClick={() => del(p.id)} className="text-terracotta hover:bg-terracotta/10 p-1.5 rounded" data-testid={`sp-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                    {p.resources && p.resources.length > 0 && (
                                        <div className="mt-2 pl-1 flex flex-wrap gap-1.5">
                                            {p.resources.map((r, i) => (
                                                <a
                                                    key={i}
                                                    href={r.type === "link" ? r.url : fileHref(r.file_id)}
                                                    target="_blank" rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-slate-200 bg-bone/40 hover:bg-bonehover text-[11px] font-semibold text-slate-700 hover:text-oxford max-w-[260px] truncate"
                                                    data-testid={`sp-resource-${p.id}-${i}`}
                                                    title={r.url || r.filename}
                                                >
                                                    {r.type === "link" ? <LinkIcon className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                                                    <span className="truncate">{r.label || r.filename || "Resurs"}</span>
                                                    {r.type === "link" && <ExternalLink className="w-2.5 h-2.5 text-slate-400" />}
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                    <div className="mt-2 flex items-center gap-2">
                                        <Link to={`/teacher/assignments?plan_id=${p.id}&student_id=${p.student_id}&subject_id=${p.subject_id}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-oxford/5 text-oxford border border-oxford/20 hover:bg-oxford hover:text-white transition" data-testid={`sp-make-assignment-${p.id}`}>
                                            <Send className="w-3 h-3" /> Krijo detyrë
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading">Cakto Plan për Nxënësit</DialogTitle></DialogHeader>
                    <form onSubmit={submit} className="space-y-3">
                        <select required value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-3" data-testid="sp-form-student">
                            <option value="">— Nxënësi —</option>
                            {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <select required value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-3" data-testid="sp-form-subject">
                            <option value="">— Lënda —</option>
                            {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-2">Niveli i mbështetjes</label>
                            <div className="grid grid-cols-3 gap-2">
                                {PLANS.map((pl) => (
                                    <button type="button" key={pl.value} onClick={() => setForm({ ...form, plan_level: pl.value })}
                                        className={`px-2 py-3 rounded-md border-2 text-center transition ${form.plan_level === pl.value ? "border-oxford bg-oxford/5" : "border-slate-200 hover:border-oxford/50"}`}
                                        data-testid={`sp-form-level-${pl.value}`}>
                                        <div className={`font-heading font-black text-lg ${pl.value === "A" ? "text-sage" : pl.value === "B" ? "text-amber-700" : "text-terracotta"}`}>{pl.label}</div>
                                        <div className="text-[10px] text-slate-500 mt-1">{pl.desc}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {templatesForLevel.length > 0 && (
                            <div className="bg-oxford/5 border border-oxford/20 rounded-md p-3">
                                <label className="text-[10px] uppercase tracking-widest font-bold text-oxford block mb-1.5">Shabllon i gatshëm (opsionale)</label>
                                <select onChange={(e) => { applyTemplate(e.target.value); e.target.value = ""; }} defaultValue="" className="h-9 w-full rounded-md border border-slate-300 bg-white text-sm px-2" data-testid="sp-form-template">
                                    <option value="">— Përzgjidh një shabllon —</option>
                                    {templatesForLevel.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                                </select>
                                <p className="text-[10px] text-slate-500 mt-1.5">Shabllonet plotësojnë shënimin + shtojnë resurse standarde (mund t'i modifikoni).</p>
                            </div>
                        )}

                        <Textarea placeholder="Shënim / arsye për këtë plan" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3} data-testid="sp-form-note" />

                        {/* Resources section */}
                        <div className="border-t border-slate-200 pt-3">
                            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-2 flex items-center gap-1.5">
                                <Paperclip className="w-3 h-3" /> Materiale / Rekomandime
                            </label>
                            <ResourceList resources={form.resources} onRemove={removeResource} />
                            {/* Add link form */}
                            <div className="mt-3 p-3 bg-bone/30 rounded-md border border-slate-200 space-y-2">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600 flex items-center gap-1.5">
                                    <LinkIcon className="w-3 h-3" /> Shto një link
                                </div>
                                <div className="grid grid-cols-[1fr_auto] gap-2">
                                    <Input value={linkInput.url} onChange={(e) => setLinkInput({ ...linkInput, url: e.target.value })} placeholder="https://..." data-testid="sp-link-url" />
                                    <Button type="button" onClick={addLink} size="sm" variant="outline" className="gap-1" data-testid="sp-link-add">
                                        <Plus className="w-3 h-3" /> Shto
                                    </Button>
                                </div>
                                <Input value={linkInput.label} onChange={(e) => setLinkInput({ ...linkInput, label: e.target.value })} placeholder="Titulli (opsionale) — p.sh., 'Video shpjegimi për derivatet'" data-testid="sp-link-label" />
                            </div>
                            {/* Upload file */}
                            <div className="mt-2 p-3 bg-bone/30 rounded-md border border-slate-200">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600 flex items-center gap-1.5 mb-2">
                                    <Upload className="w-3 h-3" /> Ngarko skedar (PDF / PPT / DOC)
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                                    onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
                                    disabled={uploading}
                                    className="text-xs text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-slate-300 file:bg-white file:text-xs file:font-bold file:text-slate-700 hover:file:bg-slate-50 file:cursor-pointer"
                                    data-testid="sp-file-input"
                                />
                                {uploading && <div className="text-[11px] text-slate-500 mt-1">Duke ngarkuar…</div>}
                            </div>
                        </div>

                        <DialogFooter>
                            <Button type="submit" disabled={busy || uploading} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="sp-form-submit">
                                {busy ? "Duke ruajtur…" : "Cakto"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
